const DEV_PREVIEW_ROUTES = [
  "/__components",
  "/__dpa",
  "/__overview",
  "/__verified",
  "/__verified-copy",
  "/preview/",
] as const;

/**
 * Preview paths that keep ClerkProvider mounted (they need sign-in UX).
 * All other `/preview/*` paths — marketplace, offers, status-card, etc. —
 * are fixture-only and bypass Clerk entirely so they work with no Clerk key
 * (required for CI Playwright baselines).
 *
 * `/preview/home` joined this list in plan 435 M2 (UI-fidelity fix item 3):
 * its `?state=gate-off`/`gate-loading` stops mount the full `HomeView` →
 * `OverviewView` → `PlatformTourController` tree, and that controller
 * "always renders inside ClerkProvider" by design (see its own doc
 * comment) — the same reason `/preview/dashboard` is on this list. The
 * route's other eleven stops render `HomeHero` directly with no Clerk
 * dependency and are unaffected either way.
 */
const CLERK_PREVIEW_ROUTES = [
  "/preview/dashboard",
  "/preview/settings",
  "/preview/home",
] as const;

export function getBrowserPathname(): string {
  if (typeof window === "undefined") {
    return "/";
  }

  try {
    const pathname = window.location?.pathname;
    return typeof pathname === "string" && pathname.length > 0 ? pathname : "/";
  } catch {
    return "/";
  }
}

export function isDevPreviewPath(pathname: unknown): boolean {
  if (!import.meta.env.DEV) {
    return false;
  }

  if (typeof pathname !== "string") {
    return false;
  }

  return DEV_PREVIEW_ROUTES.some((route) => pathname.startsWith(route));
}

/**
 * Whether a DEV preview path needs ClerkProvider mounted.
 * `/preview/dashboard` and `/preview/settings` need it; fixture-only
 * paths (marketplace/offers/status-card/etc.) do not.
 *
 * Exported so `DashboardShell` and tests share one predicate — a future
 * allowlist change shows up in both places.
 *
 * Match is exact-or-subpath: `/preview/settings` matches `/preview/settings`
 * and `/preview/settings?...` but NOT `/preview/settings-notifications`
 * (which is a distinct fixture-only route).
 */
export function previewRouteUsesClerk(pathname: string): boolean {
  return CLERK_PREVIEW_ROUTES.some(
    (p) =>
      pathname === p ||
      pathname.startsWith(p + "/") ||
      pathname.startsWith(p + "?") ||
      pathname.startsWith(p + "#"),
  );
}

/**
 * Mirrors the `shouldBypassClerk` decision made in `main.tsx`. Extracted
 * for testability so a future allowlist regression or `DashboardShell`
 * wrapper change fails CI before breaking the live screenshot seam.
 *
 * Bypass is true when ALL of:
 *  1. We are on a recognised DEV preview path.
 *  2. Either no Clerk key is present (CI / no-secrets mode) OR the path
 *     is a fixture-only preview that doesn't need Clerk mounted.
 *
 * Consequence: marketplace / offers / status-card previews always bypass
 * Clerk regardless of whether a key is set — they must render with no key.
 */
export function resolveClerkBypass(
  pathname: string,
  hasClerkKey: boolean,
): boolean {
  return isDevPreviewPath(pathname) && (!hasClerkKey || !previewRouteUsesClerk(pathname));
}
